import { useEffect } from "react";

interface BreadcrumbItem {
  name: string;
  url: string;
}

interface PageSEOConfig {
  title: string;
  description: string;
  canonical?: string;
  ogImage?: string;
  keywords?: string;
  jsonLd?: Record<string, unknown> | Record<string, unknown>[];
  breadcrumbs?: BreadcrumbItem[];
}

const BASE_URL = "https://yachtrockdetroit.com";
const DEFAULT_OG_IMAGE = "https://firebasestorage.googleapis.com/v0/b/yrd-2026.firebasestorage.app/o/Yacht%20Rock%20Detroit_Crowd%20Shot%20Sunset%20View%20of%20Stage%20and%20Audience.webp?alt=media";

function setMetaTag(property: string, content: string, isName = false) {
  const attr = isName ? "name" : "property";
  let el = document.querySelector(`meta[${attr}="${property}"]`) as HTMLMetaElement | null;
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, property);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
}

function setCanonicalLink(href: string) {
  let el = document.querySelector('link[rel="canonical"]') as HTMLLinkElement | null;
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", "canonical");
    document.head.appendChild(el);
  }
  el.setAttribute("href", href);
}

function setJsonLd(data: Record<string, unknown> | Record<string, unknown>[]) {
  // Remove any existing JSON-LD from this hook
  document.querySelectorAll('script[data-page-seo="true"]').forEach(el => el.remove());

  const items = Array.isArray(data) ? data : [data];
  items.forEach((item) => {
    const script = document.createElement("script");
    script.type = "application/ld+json";
    script.setAttribute("data-page-seo", "true");
    script.textContent = JSON.stringify(item);
    document.head.appendChild(script);
  });
}

export function usePageSEO({ title, description, canonical, ogImage, keywords, jsonLd, breadcrumbs }: PageSEOConfig) {
  useEffect(() => {
    // Title
    document.title = title;

    // Meta description
    setMetaTag("description", description, true);

    // Keywords
    if (keywords) {
      setMetaTag("keywords", keywords, true);
    }

    // Open Graph
    setMetaTag("og:title", title);
    setMetaTag("og:description", description);
    setMetaTag("og:image", ogImage || DEFAULT_OG_IMAGE);
    setMetaTag("og:type", "website");
    setMetaTag("og:site_name", "Yacht Rock Detroit");

    // Twitter
    setMetaTag("twitter:title", title, true);
    setMetaTag("twitter:description", description, true);
    setMetaTag("twitter:image", ogImage || DEFAULT_OG_IMAGE, true);

    // Canonical
    const canonicalUrl = canonical || `${BASE_URL}${window.location.pathname.replace(/\/$/, "") || "/"}`;
    setCanonicalLink(canonicalUrl);
    setMetaTag("og:url", canonicalUrl);

    // JSON-LD
    if (jsonLd) {
      setJsonLd(jsonLd);
    }

    // BreadcrumbList JSON-LD
    if (breadcrumbs && breadcrumbs.length > 0) {
      const breadcrumbJsonLd = {
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        "itemListElement": breadcrumbs.map((crumb, index) => ({
          "@type": "ListItem",
          "position": index + 1,
          "name": crumb.name,
          "item": crumb.url,
        })),
      };
      const existing = document.querySelector('script[data-breadcrumb-seo="true"]');
      if (existing) existing.remove();
      const script = document.createElement("script");
      script.type = "application/ld+json";
      script.setAttribute("data-breadcrumb-seo", "true");
      script.textContent = JSON.stringify(breadcrumbJsonLd);
      document.head.appendChild(script);
    }

    return () => {
      document.querySelectorAll('script[data-page-seo="true"]').forEach(el => el.remove());
      const breadcrumbScript = document.querySelector('script[data-breadcrumb-seo="true"]');
      if (breadcrumbScript) breadcrumbScript.remove();
    };
  }, [title, description, canonical, ogImage, keywords, jsonLd, breadcrumbs]);
}
